/**
 * Imaging diagnostics.
 *
 * Every stage of the capture → crop → studio → OCR → grading pipeline reports
 * through here, so a stuck upload can be traced from the admin debug panel or
 * from the browser console without shipping a second logging system.
 *
 * Scopes are deliberately fixed: they map 1:1 to the stages sellers can get
 * stuck on, which is what makes the log readable in a support ticket.
 */

export type ImagingScope =
  | "camera"
  | "heic"
  | "detect"
  | "crop"
  | "hero"
  | "background"
  | "ocr"
  | "grade"
  | "pipeline";

export type ImagingLogEntry = {
  id: number;
  at: number;
  scope: ImagingScope;
  message: string;
  data?: unknown;
  level: "info" | "warn" | "error";
};

const listeners = new Set<(e: ImagingLogEntry) => void>();
const buffer: ImagingLogEntry[] = [];
let seq = 0;

/** Verbose console output — opt in with `localStorage.bidnovaImagingDebug = "1"`. */
function consoleEnabled(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem("bidnovaImagingDebug") === "1";
  } catch {
    return false;
  }
}

export function imgLog(
  scope: ImagingScope,
  message: string,
  data?: unknown,
  level: ImagingLogEntry["level"] = "info",
): void {
  const entry: ImagingLogEntry = { id: ++seq, at: Date.now(), scope, message, data, level };
  buffer.push(entry);
  if (buffer.length > 200) buffer.shift();
  for (const l of [...listeners]) {
    try {
      l(entry);
    } catch {
      /* a broken listener must never break the pipeline */
    }
  }
  if (level === "error" || consoleEnabled()) {
    const line = `[imaging:${scope}] ${message}`;
    if (level === "error") console.error(line, data ?? "");
    else if (level === "warn") console.warn(line, data ?? "");
    else console.info(line, data ?? "");
  }
}

export const imgWarn = (scope: ImagingScope, message: string, data?: unknown) =>
  imgLog(scope, message, data, "warn");
export const imgError = (scope: ImagingScope, message: string, data?: unknown) =>
  imgLog(scope, message, data, "error");

export function subscribeImagingLog(fn: (e: ImagingLogEntry) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function imagingLogSnapshot(): ImagingLogEntry[] {
  return [...buffer];
}

export function clearImagingLog(): void {
  buffer.length = 0;
}
